import React, { useRef, useEffect, useState, useCallback } from 'react';

import { resolvePlayerSdkUrl, isDefaultPlayerSdk } from '../../../lib/playerSdk';
import { applyThemeToHost, extractTheme } from '../../../lib/themeTokens';
import { resolvePreviewTarget } from '../../../lib/demoPreviewEvent';

/** Same-origin host page for the preview (see public/theme-preview.html). */
const PREVIEW_PAGE = '/theme-preview.html';

/**
 * How long to coalesce preference pushes, in ms.
 *
 * A push re-normalizes the whole bag and re-renders the player tree, and a
 * colour input fires continuously while it is dragged. Short enough to still
 * read as live -- this is well under the ~100ms where a change stops feeling
 * attached to the gesture that caused it.
 */
const PUSH_DEBOUNCE_MS = 60;

/**
 * Widths the preview can be pinned to, so a theme can be checked against the
 * layouts the player actually renders rather than only the one the admin's
 * monitor happens to produce. `null` means "take the pane", which is the
 * default and the widest case.
 *
 * Only offered in pane mode: the drawer is already narrower than the tablet
 * breakpoint, so pinning inside it would compare two narrow layouts.
 */
const VIEWPORTS = [
    { key: 'desktop', label: 'Desktop', width: null },
    { key: 'tablet', label: 'Tablet', width: 820 },
    { key: 'narrow', label: 'Narrow', width: 420 },
];

/**
 * The theme preview: a real player, in one of two shells.
 *
 * TWO SHELLS, ONE MOUNT
 * `mode="pane"` is the Theme Builder's right-hand column -- always visible,
 * no dismiss, the wide half of a split. `mode="drawer"` is the overlay the
 * other nine tabs open from the header. They are the same component, and the
 * caller keeps it at ONE position in the tree across a mode switch, because
 * anything that re-parents the <iframe> unmounts it and reboots the player.
 *
 * Why a pane for Theme and a drawer for everything else. The drawer's own
 * original argument was that a side column squeezes the player into a width
 * that misrepresents its layout -- true when controls and preview compete at
 * roughly equal weight, which is what the stacked-card Theme tab did. The
 * rebuilt Theme tab inverts the ratio: a ~34rem control rail and the preview
 * takes the rest, which is WIDER than the drawer, not narrower. On the other
 * tabs the controls still want the full width and the preview is an
 * occasional check, so the overlay remains right there.
 *
 * The iframe matters in both shells: the player's Share / Download dialogs
 * are native `<dialog>` + `showModal()`, which render in the TOP LAYER and
 * would otherwise cover the whole admin UI and inert it. An iframe has its
 * own top layer, so they stay in the box.
 *
 * WHAT IT PREVIEWS AGAINST
 * One shared demo event, for every client (see `lib/demoPreviewEvent.js`).
 * There is no event picker: the client's own events were arbitrary, and one
 * without a transcript, documents or agenda left the tab strip empty, so the
 * tab controls painted onto nothing.
 *
 * HOW IT STAYS LIVE
 * The preference is handed to the player as an object -- `playerPref` at
 * launch, `Invintus.setPreference()` after -- so EVERY preference previews
 * live, including unsaved edits and including non-theme settings, which
 * previously only appeared after a save. On a player build that predates
 * those two surfaces it falls back to painting `--player-*` custom
 * properties into the frame directly, which themes correctly but leaves
 * non-theme settings showing the demo account's own.
 */
export default function ThemePreview (props) {
    const {
        preference,
        prefID,
        mode = 'drawer',
        isOpen,
        onOpenChange,
        darkPage,
        onDarkPageChange,
    } = props;

    const isPane = mode === 'pane';

    const frameRef = useRef(null);
    // Resolved once per mount: the URL parameter also persists the choice,
    // so re-reading on every render would fight a later `?playerSdk=reset`.
    const sdkUrl = useRef(resolvePlayerSdkUrl()).current;
    // Likewise once -- an override is per page load, and re-reading mid-edit
    // would reload the frame out from under the admin.
    const target = useRef(resolvePreviewTarget()).current;

    // Owned by the editor root, not here: the contrast audit measures against
    // whatever page the preview is set to, and the rail needs to read the same
    // answer. It is the user's statement about their own site, so one place
    // should hold it.
    const setDarkPage = onDarkPageChange;
    const [viewport, setViewport] = useState('desktop');
    // Which channel the frame actually took, reported by the host page after
    // it feature-detects. Drives the fallback painting below.
    const [inlineMode, setInlineMode] = useState(false);

    // The live preference, readable by the host page at launch. A ref behind
    // a function so the page can never read a stale value: the SDK loads
    // asynchronously, and the admin may edit something while it does.
    const preferenceRef = useRef(preference);
    preferenceRef.current = preference;

    useEffect(() => {
        window.__invintusPreviewPreference = () => preferenceRef.current;
        return () => { delete window.__invintusPreviewPreference; };
    }, []);

    // The host page reports which path it took, so the fallback painting is
    // driven by what actually happened rather than a second feature check.
    useEffect(() => {
        function onMessage (ev) {
            if (ev.origin !== window.location.origin) return;
            if (!ev.data || ev.data.type !== 'invintus-preview-ready') return;
            setInlineMode(Boolean(ev.data.inline));
        }
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, []);

    const previewUrl = useCallback(() => (
        PREVIEW_PAGE +
        '?clientID=' + encodeURIComponent(target.clientID) +
        '&eventID=' + encodeURIComponent(target.eventID) +
        // Only meaningful when an override has pointed this at a real
        // client's own event. Against the demo account a prefID belongs to
        // a different client, and the API answers that with a null row.
        (!target.isDemo && prefID ? '&prefID=' + encodeURIComponent(prefID) : '') +
        '&sdk=' + encodeURIComponent(sdkUrl)
    ), [target, prefID, sdkUrl]);

    const paintPage = useCallback((doc) => {
        if (!doc || !doc.body) return;
        // The player paints no background of its own, so the frame stands in
        // for the client's page.
        doc.body.style.background = darkPage ? '#1b1b1b' : '#ffffff';
    }, [darkPage]);

    const push = useCallback(() => {
        const frame = frameRef.current;
        if (!frame) return;
        let doc;
        let win;
        try {
            doc = frame.contentDocument;
            win = frame.contentWindow;
        } catch (e) {
            // Would only happen if the frame somehow went cross-origin.
            return;
        }
        if (!doc) return;

        paintPage(doc);

        const invintus = win && win.Invintus;
        if (inlineMode && invintus && typeof invintus.setPreference === 'function') {
            // The whole preference, so non-theme edits preview too.
            invintus.setPreference(preferenceRef.current);
            return;
        }

        // Pre-inline player build: theme only, painted straight onto the
        // mount. Same set-or-remove atom the player runs at boot.
        applyThemeToHost(doc.getElementById('mount'), extractTheme(preferenceRef.current));
    }, [inlineMode, paintPage]);

    // Coalesce: a colour drag fires continuously, and each push re-normalizes
    // and re-renders the player.
    const timerRef = useRef(null);
    useEffect(() => {
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(push, PUSH_DEBOUNCE_MS);
        return () => clearTimeout(timerRef.current);
    }, [preference, push]);

    const handleFrameLoad = useCallback(() => {
        push();
        // The SDK mounts asynchronously inside; repaint once it has.
        setTimeout(push, 400);
    }, [push]);

    // Drawer mode hides itself; pane mode is the layout and is always shown.
    if (!isPane && !isOpen) return null;

    const active = VIEWPORTS.find(v => v.key === viewport) || VIEWPORTS[0];

    return (
        /* Non-modal on purpose in BOTH shells: no backdrop, nothing inerted,
           so the controls stay live on the left while this sits on the right. */
        <aside
            className={'theme-drawer' + (isPane ? ' theme-drawer--pane' : '')}
            aria-label="Theme preview"
        >
            <header className="theme-drawer__head">
                {/* Close sits on the leading edge, nearest the controls you
                    are returning to. Pane mode has nothing to return to --
                    the preview IS half the screen -- so it has no close. */}
                {/* An icon, not the `×` character. The multiplication
                    sign sits on the font's math axis rather than the
                    optical centre and is drawn lighter than the label
                    beside it, so it read as slightly off. The rest of
                    the editor already uses Font Awesome (fa-save,
                    fa-circle-notch, fa-exclamation-triangle). */}
                {!isPane ? (
                    <button
                        type="button"
                        className="theme-drawer__close"
                        onClick={() => onOpenChange(false)}
                        aria-label="Close preview"
                        title="Close preview"
                    >
                        <i className="fa fa-times" aria-hidden="true"/>
                    </button>
                ) : null}

                <h4 className="theme-drawer__title">
                    Preview
                    {/* Say so when this is not the stock player.
                        A preview silently pointed at some other
                        build is worse than no preview. */}
                    {!isDefaultPlayerSdk(sdkUrl) ? (
                        <span className="label label-info theme-drawer__source" title={sdkUrl}>
                            custom build
                        </span>
                    ) : null}
                    {/* Same argument for the event. The demo event is
                        the norm and needs no label; an override is the
                        surprising state and must announce itself. */}
                    {!target.isDemo ? (
                        <span
                            className="label label-warning theme-drawer__source"
                            title={'clientID ' + target.clientID + ' / eventID ' + target.eventID}
                        >
                            custom event
                        </span>
                    ) : null}
                </h4>

                {/* The player's own layout changes at its breakpoints, and a
                    theme that reads well wide can collapse narrow -- the tab
                    strip in particular. Only in pane mode: the drawer is
                    already narrower than the tablet width, so the options
                    would compare two narrow layouts against each other. */}
                {isPane ? (
                    <div className="theme-drawer__page">
                        <span className="theme-drawer__page-label">Width</span>
                        <div
                            className="theme-drawer__page-toggle"
                            role="group"
                            aria-label="Preview width"
                        >
                            {VIEWPORTS.map(v => (
                                <button
                                    key={v.key}
                                    type="button"
                                    className={'theme-drawer__page-option' + (v.key === viewport ? ' is-active' : '')}
                                    aria-pressed={v.key === viewport}
                                    onClick={() => setViewport(v.key)}
                                >
                                    {v.label}
                                </button>
                            ))}
                        </div>
                    </div>
                ) : null}

                {/* A labelled two-state control rather than a link
                    reading "Dark page", which never said whether it
                    described the current state or the action.

                    Built from the Theme tab's own chip vocabulary --
                    a pill, a swatch dot, the same blue selected ring
                    as a preset chip -- rather than Bootstrap's
                    btn-group. The toolbar sits directly above the
                    controls it belongs to, and a stock grey button
                    pair was the one piece of the screen that read as
                    borrowed from somewhere else. */}
                <div className="theme-drawer__page">
                    <span className="theme-drawer__page-label">Page</span>
                    <div
                        className="theme-drawer__page-toggle"
                        role="group"
                        aria-label="Preview page background"
                    >
                        <button
                            type="button"
                            className={'theme-drawer__page-option' + (darkPage ? '' : ' is-active')}
                            aria-pressed={!darkPage}
                            onClick={() => setDarkPage(false)}
                        >
                            <i className="theme-drawer__page-dot theme-drawer__page-dot--light" aria-hidden="true"/>
                            Light
                        </button>
                        <button
                            type="button"
                            className={'theme-drawer__page-option' + (darkPage ? ' is-active' : '')}
                            aria-pressed={darkPage}
                            onClick={() => setDarkPage(true)}
                        >
                            <i className="theme-drawer__page-dot theme-drawer__page-dot--dark" aria-hidden="true"/>
                            Dark
                        </button>
                    </div>
                </div>
            </header>

            {/* The width pin constrains the FRAME, not the pane, and the
                surround stays visible so a pinned preview reads as a
                deliberate narrowing rather than as the pane having shrunk. */}
            <div className={'theme-drawer__stage' + (active.width ? ' theme-drawer__stage--pinned' : '')}>
                <iframe
                    ref={frameRef}
                    key={previewUrl()}
                    src={previewUrl()}
                    title="Theme preview"
                    className="theme-drawer__frame"
                    style={active.width ? { width: active.width + 'px' } : undefined}
                    onLoad={handleFrameLoad}
                    // Same-origin by design -- the host page reads the
                    // live preference off the parent, and the fallback
                    // path paints theme tokens into the document. No
                    // sandbox attribute: it would force an opaque origin
                    // and break exactly the access this depends on.
                />
            </div>
        </aside>
    );
}

/**
 * The header trigger, split out from the preview itself.
 *
 * They used to be one component so the button could sit in the editor header
 * while the fixed-position drawer overlaid the right edge from the same
 * render. The pane needs to be a real child of the editor body to take a
 * column there, and the button still belongs in the header -- so the two
 * parted ways. Theme mode renders no trigger at all: the preview is not
 * dismissible there, so a toggle would have nothing to say.
 */
export function ThemePreviewToggle ({ isOpen, onOpenChange }) {
    return (
        <div className="theme-preview-trigger">
            <button
                type="button"
                className={'btn ' + (isOpen ? 'btn-default' : 'btn-primary')}
                onClick={() => onOpenChange(!isOpen)}
                title="Open the real player and watch your changes apply live"
            >
                {isOpen ? 'Hide preview' : 'Show preview'}
            </button>
        </div>
    );
}
